/**
 * Zahlungsraten einer Bestellung - Unterressource `PATCH /api/orders/:id/payments/:payId`
 * (`backend/src/http/routes/orderPayments.ts:109`). Derselbe Koerperbau wie `api/aufgaben.ts`:
 * `{ rowVersion, felder }` per PATCH.
 *
 * BERICHTIGUNG (Task 5a): der Satz "nicht das flache `{ ...daten, rowVersion }` von
 * `api/bestellungen.ts#ressource()`" stand hier als Gegensatz. Den gibt es nicht - `PUT
 * /api/orders/:id` existiert im Backend nicht, und `bestellungAendern` sendet seit Task 5a ebenfalls
 * `{ rowVersion, felder }` per PATCH. Siehe Kopfkommentar von `api/aufgaben.ts`.
 *
 * NOCH KEIN AUFRUFER (benannte Luecke): `ZahlungTab.tsx#patch` arbeitet weiterhin auf dem Altarray
 * `order.payments[]` und schreibt es als Ganzes ueber `onAendern` - dieser Weg wirft seit Task 5a
 * laut (`FeldOhneGruppeFehler`). Der urspruengliche Grund dafuer, dass hier bis heute niemand ruft -
 * `GET /api/orders/:id` liefere die Raten nicht mit, und `OrderPayment` (`lib/types.ts`) fuehre kein
 * eigenes `rowVersion` - gilt seit 2026-08-11 nicht mehr: `orders.ts#ladeBestellung` liefert die
 * Raten samt `rowVersion` flach mit (dieselbe Messung wie `api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`,
 * Eintrag "payments"). Was bleibt, ist die fehlende Verdrahtung in `ZahlungTab.tsx`, nicht die
 * fehlende Route.
 */
import { anfrage } from "./basis.ts";
import type { OrderPayment } from "../lib/types.ts";

/**
 * `PATCH /api/orders/:id/payments/:payId`. `felder` ist ein nicht-leeres Objekt der zu aendernden
 * Raten-Spalten (camelCase). Liefert den oeffentlichen Ratendatensatz nach dem Schreiben.
 */
export function ratenAendern(
  bestellungId: string,
  payId: string,
  felder: Record<string, unknown>,
  rowVersion: number,
): Promise<OrderPayment> {
  return anfrage<OrderPayment>(
    `/api/orders/${encodeURIComponent(bestellungId)}/payments/${encodeURIComponent(payId)}`,
    { methode: "PATCH", koerper: { rowVersion, felder } },
  );
}

// `alsRate` (Naht fuer vom Server geladene Raten: `dueDate`/`paidAt` -> `due`/`paidDate`) wohnt seit
// dem KI-Zugang (Stufe 1) in `lib/bestellungDraht.ts`, neben `entschachtele`, das sie ruft: der Server
// faehrt dieselbe Umformung. Der Name bleibt hier erreichbar.
export { alsRate } from "../lib/bestellungDraht.ts";

/** `spalte_mit_unterstrich` -> `spalteMitUnterstrich` - Gegenstueck zu `camelSchluessel` im Backend. */
function alsCamelCase(spalte: string): string {
  return spalte.replace(/_([a-z0-9])/g, (_treffer, zeichen: string) => zeichen.toUpperCase());
}

/**
 * `POST /api/orders/:id/payments`. Der Router (`orderPayments.ts:78-129`) erwartet die Felder FLACH
 * und in CAMELCASE im Koerper (nicht unter `felder` verschachtelt wie beim PATCH) - er uebersetzt
 * jeden bekannten camelCase-Schluessel selbst zurueck zu seiner Spalte
 * (`ratenFelderAusKoerper`). `sortIndex` ist Pflicht (400 ohne sie).
 */
export function rateAnlegen(
  bestellungId: string,
  felder: Record<string, unknown>,
): Promise<OrderPayment> {
  const koerper = Object.fromEntries(
    Object.entries(felder).map(([spalte, wert]) => [alsCamelCase(spalte), wert]),
  );
  return anfrage<OrderPayment>(`/api/orders/${encodeURIComponent(bestellungId)}/payments`, {
    methode: "POST",
    koerper,
  });
}

/** `DELETE /api/orders/:id/payments/:payId`. */
export function rateLoeschen(
  bestellungId: string,
  payId: string,
  rowVersion: number,
): Promise<void> {
  return anfrage<void>(
    `/api/orders/${encodeURIComponent(bestellungId)}/payments/${encodeURIComponent(payId)}`,
    { methode: "DELETE", koerper: { rowVersion } },
  );
}
